/**
 * Color processing utilities for LAB color space conversions and analysis
 */

import { percentileAxis0 } from './dataUtils'

export interface StepMetrics {
  medLab: [number, number, number]
  ratioAb: number
  medShiftAb: number
  deMed: number
}

/**
 * Represents an RGB color with values in range [0, 255]
 */

export interface Point {
  x: number
  y: number
}

export interface RegionPolygon {
  points: Point[]
  color: Color
  pixelCount: number
  bounds: { minX: number; minY: number; maxX: number; maxY: number }
}
/**
 * Represents a LAB color
 * L: lightness (0-100)
 * a: green-red axis
 * b: blue-yellow axis
 */
export interface LAB {
  L: number
  a: number
  b: number
}

/**
 * Computes Euclidean distance in LAB space (ΔE76)
 * Used to compare outer probes to the center probe
 *
 * @param lab1 - First LAB color [L, a, b]
 * @param lab2 - Second LAB color [L, a, b]
 * @returns Distance in LAB space
 */
export function deltaE76(lab1: number[], lab2: number[]): number {
  const dL = lab1[0] - lab2[0]
  const da = lab1[1] - lab2[1]
  const db = lab1[2] - lab2[2]

  return Math.sqrt(dL * dL + da * da + db * db)
}

/**
 * Robust summary statistics for a set of LAB pixels
 * Used in probe_green_touching_circles to decide the green's colors
 *
 * @param labPixels - Array of LAB pixels
 * @returns Summary statistics including median, percentiles, and spread
 */
export function robustLabSummary(labPixels: number[][]): {
  med: number[]
  p16: number[]
  p84: number[]
  spread: number[]
  n: number
} {
  const n = labPixels.length

  // Extract channels
  const L = labPixels.map((p) => p[0]).sort((a, b) => a - b)
  const a = labPixels.map((p) => p[1]).sort((a, b) => a - b)
  const b = labPixels.map((p) => p[2]).sort((a, b) => a - b)

  const percentile = (arr: number[], p: number): number => {
    const idx = Math.floor((p / 100) * (arr.length - 1))
    return arr[idx]
  }

  const med = [percentile(L, 50), percentile(a, 50), percentile(b, 50)]

  const p16 = [percentile(L, 16), percentile(a, 16), percentile(b, 16)]

  const p84 = [percentile(L, 84), percentile(a, 84), percentile(b, 84)]

  const spread = [p84[0] - p16[0], p84[1] - p16[1], p84[2] - p16[2]]

  return { med, p16, p84, spread, n }
}

/**
 * Computes core per-step metrics for ray walking
 *
 * @param labPixels - Array of LAB pixels from a patch
 * @param baseLab - Baseline LAB color
 * @param abGate - AB distance gate threshold
 * @param Lw - Weight for L channel (default 0.35)
 * @returns Metrics including median LAB, ratio, shifts, and distances
 */
export function squareDeMetrics(
  labPixels: Float32Array,
  baseLab: Float32Array,
  abGate: number,
  Lw = 0.35,
): StepMetrics {
  const n = labPixels.length / 3
  const med = percentileAxis0(labPixels, n, 50)

  let countInGate = 0
  const deValues: number[] = []

  for (let i = 0; i < n; i++) {
    const a = labPixels[i * 3 + 1]
    const b = labPixels[i * 3 + 2]
    const dA = a - baseLab[1]
    const dB = b - baseLab[2]
    const dAb = Math.sqrt(dA * dA + dB * dB)
    if (dAb <= abGate) {
      countInGate++
    }

    const dL = labPixels[i * 3] - baseLab[0]
    const dE = Math.sqrt((Lw * dL) ** 2 + dAb ** 2)
    deValues.push(dE)
  }

  deValues.sort((a, b) => a - b)
  const midIdx = Math.floor(deValues.length / 2)
  const deMed = deValues.length % 2 === 1 ? deValues[midIdx] : (deValues[midIdx - 1] + deValues[midIdx]) / 2

  const medShiftAb = Math.sqrt((med[1] - baseLab[1]) ** 2 + (med[2] - baseLab[2]) ** 2)

  return {
    medLab: [med[0], med[1], med[2]],
    ratioAb: countInGate / n,
    medShiftAb,
    deMed,
  }
}

// ─────────────────────────────────────────────
// Pixel Sampling Helpers
// ─────────────────────────────────────────────

/**
 * Sample LAB pixels inside a circular area in the ROI.
 * roiRgba: Uint8ClampedArray (RGBA), W/H: ROI dimensions.
 * Returns Float32Array of [L,a,b] triplets for valid pixels, or null.
 */
export function circleLabPixels(
  roiRgba: ImageData,
  W: number,
  H: number,
  validMask: Uint8Array | null,
  centerX: number,
  centerY: number,
  radiusPx: number,
): Float32Array | null {
  const r = radiusPx
  const x0 = Math.max(0, Math.floor(centerX - r))
  const y0 = Math.max(0, Math.floor(centerY - r))
  const x1 = Math.min(W, Math.ceil(centerX + r + 1))
  const y1 = Math.min(H, Math.ceil(centerY + r + 1))
  if (x1 <= x0 || y1 <= y0) {
    return null
  }

  const rSq = r * r
  const data = roiRgba.data
  const pixels: number[] = [] // collect R,G,B,A quads

  for (let py = y0; py < y1; py++) {
    for (let px = x0; px < x1; px++) {
      const dx = px - centerX
      const dy = py - centerY
      if (dx * dx + dy * dy > rSq) {
        continue
      }
      if (validMask && validMask[py * W + px] === 0) {
        continue
      }

      const idx = (py * W + px) * 4
      pixels.push(data[idx], data[idx + 1], data[idx + 2], data[idx + 3])
    }
  }

  if (pixels.length === 0) {
    return null
  }

  const n = pixels.length / 4
  const rgba = new Uint8ClampedArray(pixels)
  return rgbaToLab(rgba, n)
}

/**
 * Sample LAB pixels from a square patch.
 * Returns [labPixels (Nx3 Float32Array), bbox] or [null, bbox].
 */
export function squareLabPixels(
  roiRgba: Uint8ClampedArray,
  W: number,
  H: number,
  validMask: Uint8Array | null,
  centerX: number,
  centerY: number,
  patchHalf: number,
): [Float32Array | null, [number, number, number, number]] {
  const cx = Math.round(centerX)
  const cy = Math.round(centerY)
  const x0 = Math.max(0, cx - patchHalf)
  const y0 = Math.max(0, cy - patchHalf)
  const x1 = Math.min(W, cx + patchHalf + 1)
  const y1 = Math.min(H, cy + patchHalf + 1)
  const bbox: [number, number, number, number] = [x0, y0, x1, y1]

  if (x1 <= x0 || y1 <= y0) {
    return [null, bbox]
  }

  const pixels: number[] = []
  for (let py = y0; py < y1; py++) {
    for (let px = x0; px < x1; px++) {
      if (validMask && validMask[py * W + px] === 0) {
        continue
      }
      const idx = (py * W + px) * 4
      pixels.push(roiRgba[idx], roiRgba[idx + 1], roiRgba[idx + 2], roiRgba[idx + 3])
    }
  }

  if (pixels.length === 0) {
    return [null, bbox]
  }

  const n = pixels.length / 4
  const rgba = new Uint8ClampedArray(pixels)
  return [rgbaToLab(rgba, n), bbox]
}

/**
 * Convert an RGBA pixel array to LAB values.
 * Returns Float32Array of [L, a, b] triplets (length = numPixels * 3).
 *
 * This is a direct port of the Python rgb_to_lab() function.
 * Uses the same scaling: L→[0,255], a→centered at 128, b→centered at 128.
 *
 * For a library alternative, use `color-convert` or `d3-color`:
 *   import convert from 'color-convert';
 *   const [L, a, b] = convert.rgb.lab(r, g, b);
 */
export function rgbaToLab(rgba: Uint8ClampedArray, numPixels: number): Float32Array {
  const lab = new Float32Array(numPixels * 3)
  const Xn = 0.95047,
    Yn = 1.0,
    Zn = 1.08883
  const eps = 216 / 24389
  const k = 24389 / 27

  function f(t: number): number {
    return t > eps ? Math.cbrt(t) : (k * t + 16) / 116
  }

  for (let i = 0; i < numPixels; i++) {
    const idx = i * 4
    // sRGB → linear
    let r = rgba[idx] / 255
    let g = rgba[idx + 1] / 255
    let b = rgba[idx + 2] / 255

    r = r <= 0.04045 ? r / 12.92 : ((r + 0.055) / 1.055) ** 2.4
    g = g <= 0.04045 ? g / 12.92 : ((g + 0.055) / 1.055) ** 2.4
    b = b <= 0.04045 ? b / 12.92 : ((b + 0.055) / 1.055) ** 2.4

    // linear RGB → XYZ
    const X = 0.4124564 * r + 0.3575761 * g + 0.1804375 * b
    const Y = 0.2126729 * r + 0.7151522 * g + 0.072175 * b
    const Z = 0.0193339 * r + 0.119192 * g + 0.9503041 * b

    const fx = f(X / Xn)
    const fy = f(Y / Yn)
    const fz = f(Z / Zn)

    const L = 116 * fy - 16 // 0..100
    const a_ = 500 * (fx - fy)
    const b_ = 200 * (fy - fz)

    // Scale to match Python output: L→[0,255], a/b centered at 128
    const out = i * 3
    lab[out] = (L * 255.0) / 100.0
    lab[out + 1] = a_ + 128.0
    lab[out + 2] = b_ + 128.0
  }

  return lab
}

/**
 * Fetch a single XYZ tile as ImageData.
 * In browser: draws to an offscreen canvas to get pixel data.
 * In Node: use sharp or Jimp to decode.
 */
async function blobToDataUrl(blob: Blob): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
        return
      }

      reject(new Error('Failed to convert blob to data URL'))
    }

    reader.onerror = () => {
      reject(reader.error ?? new Error('Failed to read blob'))
    }

    reader.readAsDataURL(blob)
  })
}

function logImageToConsole(dataUrl: string, width: number, height: number, label: string): void {
  const scale = width > 256 || height > 256 ? 0.75 : 1
  const style = [
    'font-size: 1px;',
    `padding: ${Math.max(1, Math.round((height * scale) / 2))}px ${Math.max(1, Math.round((width * scale) / 2))}px;`,
    `background: url(${dataUrl}) no-repeat;`,
    `background-size: ${Math.round(width * scale)}px ${Math.round(height * scale)}px;`,
    'background-position: center;',
    'color: transparent;',
  ].join(' ')

  console.log(label)
  console.log('%c ', style)
}

export async function fetchTileRgb(
  lat: number,
  lng: number,
  zoom: number,
  siteConfig?: { googleMapsApiKey?: string } | null,
): Promise<ImageData> {
  if (!siteConfig?.googleMapsApiKey) {
    console.error('Google Maps API key is required to fetch tile RGB data')
    return new ImageData(1, 1)
  }
  const url =
    `https://maps.googleapis.com/maps/api/staticmap?` +
    `center=${lat},${lng}&zoom=${zoom}&size=256x256&maptype=satellite&` +
    `key=${siteConfig.googleMapsApiKey}`

  console.info('Green auto-map source image', {
    lat,
    lng,
    zoom,
    staticMapUrl: url,
  })

  const response = await fetch(url)
  const blob = await response.blob()
  const dataUrl = await blobToDataUrl(blob)
  const bitmap = await createImageBitmap(blob)

  logImageToConsole(dataUrl, bitmap.width, bitmap.height, 'Green auto-map source image preview')

  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height)
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(bitmap, 0, 0)
  return ctx.getImageData(0, 0, bitmap.width, bitmap.height)
}

export interface Color {
  r: number
  g: number
  b: number
  a: number
}

/**
 * Samples colors from a circular area around a center point and returns the average color
 * @param imageData - The image data to sample from
 * @param centerX - Center X coordinate
 * @param centerY - Center Y coordinate
 * @param radius - Radius of the sampling circle in pixels
 * @param validMask - Optional mask to exclude certain pixels (1 = valid, 0 = invalid)
 * @returns Average color from the circular sample area
 */
export const sampleCircularArea = (
  imageData: ImageData,
  centerX: number,
  centerY: number,
  radius: number = 5,
): Color & { sampleCount: number } => {
  let totalR = 0
  let totalG = 0
  let totalB = 0
  let totalA = 0
  let count = 0

  const minX = Math.max(0, Math.floor(centerX - radius))
  const maxX = Math.min(imageData.width - 1, Math.ceil(centerX + radius))
  const minY = Math.max(0, Math.floor(centerY - radius))
  const maxY = Math.min(imageData.height - 1, Math.ceil(centerY + radius))

  const radiusSquared = radius * radius

  // Sample all pixels within the circular area
  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      // Check if pixel is within circular radius
      const dx = x - centerX
      const dy = y - centerY
      const distSquared = dx * dx + dy * dy

      if (distSquared <= radiusSquared) {
        const idx = (y * imageData.width + x) * 4
        totalR += imageData.data[idx]
        totalG += imageData.data[idx + 1]
        totalB += imageData.data[idx + 2]
        totalA += imageData.data[idx + 3]
        count++
      }
    }
  }

  // Return average color
  return {
    r: Math.round(totalR / count),
    g: Math.round(totalG / count),
    b: Math.round(totalB / count),
    a: Math.round(totalA / count),
    sampleCount: count,
  }
}

/**
 * Smooths a polygon by filtering outliers and applying Chaikin's corner-cutting algorithm
 * This reduces indents and makes the shape more circular/regular
 */
export const smoothPolygon = (points: number[][], iterations: number = 3): Array<{ x: number; y: number }> => {
  if (points.length < 3) {
    return points.map((pt) => ({ x: pt[0], y: pt[1] }))
  }

  // Use lat lng instead of x, y
  let smoothed = points.map((pt) => ({ x: pt[0], y: pt[1] }))

  for (let iter = 0; iter < iterations; iter++) {
    const newPoints: Array<{ x: number; y: number }> = []
    const n = smoothed.length

    for (let i = 0; i < n; i++) {
      const p0 = smoothed[i]
      const p1 = smoothed[(i + 1) % n]

      // Add original point
      newPoints.push(p0)

      // Add midpoint between p0 and p1
      newPoints.push({
        x: (p0.x + p1.x) / 2,
        y: (p0.y + p1.y) / 2,
      })
    }

    smoothed = newPoints
  }

  return smoothed
}
